<template>
  <div class="container-fluid">
    <div class="infoTitle">
      <p>Dashboard</p>
    </div>

    <div class="row mb-4">
      <div class="col-md-3">
        <div class="card">
          <div class="card-body text-center">
            <h2 class="single-stat">{{ totalCategories }}</h2>
            <small class="text-muted">Categorie configurate</small>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card">
          <div class="card-body text-center">
            <h2 class="single-stat">{{ totalSites }}</h2>
            <small class="text-muted">Origini monitorate</small>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card">
          <div class="card-body text-center">
            <h2 class="single-stat">{{ successfulSites }}</h2>
            <small class="text-muted">Origini attive</small>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card">
          <div class="card-body text-center">
            <h2 class="single-stat" :class="getSuccessRateClass(globalSuccessRate)">{{ globalSuccessRate }}%</h2>
            <small class="text-muted">Percentuale globale scraping</small>
          </div>
        </div>
      </div>
    </div>

    <div class="infoTitle">
      <p>Lista origini | 19 Novembre 2025</p>
    </div>

    <div v-if="isLoading && loadingProgress.total > 0" class="loading-banner mb-3">
      <div class="d-flex align-items-center justify-content-center gap-3">
        <div class="loading-spinner"></div>
        <div>
          <strong>Caricamento in corso...</strong>
          <span class="ms-2">{{ loadingProgress.current }} / {{ loadingProgress.total }} categorie caricate</span>
        </div>
      </div>
      <div class="progress mt-2" style="height: 6px;">
        <div
          class="progress-bar progress-bar-striped progress-bar-animated"
          role="progressbar"
          :style="{ width: `${(loadingProgress.current / loadingProgress.total) * 100}%` }"
          :aria-valuenow="loadingProgress.current"
          :aria-valuemin="0"
          :aria-valuemax="loadingProgress.total">
        </div>
      </div>
    </div>

    <div v-else-if="isLoading && !categoriesData.length" class="text-center py-5">
      <div class="loading-spinner me-2"></div>
      <span>Scraping in corso</span>
    </div>

    <div v-else-if="hasError" class="alert alert-danger">
      <strong>Errore:</strong> {{ errorMessage }}
      <button class="btn btn-sm btn-outline-danger ms-2" @click="loadAllData">Riprova</button>
    </div>

    <!-- Categories Table -->
    <div v-else-if="categoriesData.length" class="table-responsive">
      <table class="table table-striped table-hover">
        <thead>
          <tr>
            <th class="headCol">Categoria</th>
            <th class="headCol">Siti Totali</th>
            <th class="headCol">Siti Attivi</th>
            <th class="headCol">Ultimo Aggiornamento</th>
            <th class="headCol"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="category in categoriesData" :key="category.name">
            <td class="bodyCol">
              <router-link :to="`/category/${category.name}`" class="site-link">
                {{ formatCategoryName(category.name) }}
              </router-link>
            </td>
            <td class="bodyCol">{{ category.totalSites }}</td>
            <td class="bodyCol">
              <span :class="[
                'stato-chip',
                category.successRate === 100
                  ? 'stato-success'
                  : category.successRate >= 50
                    ? 'stato-warning'
                    : 'stato-error',
              ]">
                {{ category.successfulSites }} di {{ category.totalSites }}
              </span>
            </td>
            <td class="bodyCol">
              <span class="scraping-timestamp">
                {{ formatDate(category.lastUpdate) }}
              </span>
            </td>
            <td class="text-end">
              <router-link :to="`/category/${category.name}`" title="Vai alla categoria">
                <img :src="goIconUrl" alt="Apri sito sorgente" class="icon--small" />
              </router-link>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-else class="text-center py-5">
      <div class="alert alert-info">
        <h5>Nessuna categoria trovata</h5>
        <p>Assicurati che ci siano file di configurazione nella directory Config del backend.</p>
        <button class="btn btn-primary" @click="loadAllData">Ricarica</button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import {
  getCategories,
  getCategorySummary,
  type CategorySummaryResponse,
} from '@/services/scrapingService'
import { format } from 'date-fns'
import { it } from 'date-fns/locale'
import goIconUrl from '@/assets/icon/arrow-right.svg?url'

interface CategoryData {
  name: string
  totalSites: number
  successfulSites: number
  successRate: number
  lastUpdate: string
}

const categoriesData = ref<CategoryData[]>([])
const isLoading = ref(false)
const hasError = ref(false)
const errorMessage = ref('')
const loadingProgress = ref({ current: 0, total: 0 })

const totalCategories = computed(() => categoriesData.value.length)
const totalSites = computed(() =>
  categoriesData.value.reduce((sum, cat) => sum + cat.totalSites, 0),
)
const successfulSites = computed(() =>
  categoriesData.value.reduce((sum, cat) => sum + cat.successfulSites, 0),
)
const globalSuccessRate = computed(() => {
  if (totalSites.value === 0) return 0
  return Math.round((successfulSites.value / totalSites.value) * 100)
})

const formatCategoryName = (category: string): string => {
  return category.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
}

const formatDate = (dateString: string): string => {
  try {
    return format(new Date(dateString), 'dd/MM/yyyy HH:mm', { locale: it })
  } catch {
    return 'Data non valida'
  }
}

const getSuccessRateClass = (rate: number): string => {
  if (rate >= 71) return 'success-rate-high'
  if (rate >= 41) return 'success-rate-medium'
  return 'success-rate-low'
}

const loadAllData = async () => {
  try {
    isLoading.value = true
    hasError.value = false
    errorMessage.value = ''
    categoriesData.value = [] // Reset dei dati

    const categoriesResponse = await getCategories()
    const categories = categoriesResponse.data.categories || []

    if (categories.length === 0) {
      categoriesData.value = []
      isLoading.value = false
      return
    }

    // Progressive loading: carica categorie una alla volta
    const loadedCategories: CategoryData[] = []
    loadingProgress.value = { current: 0, total: categories.length }

    for (let i = 0; i < categories.length; i++) {
      const categoryName = categories[i]
      try {
        console.log(`Caricamento progressivo [${i + 1}/${categories.length}]: ${categoryName}`)
        const summaryResponse = await getCategorySummary(categoryName)
        const summary: CategorySummaryResponse = summaryResponse.data

        const categoryData = {
          name: categoryName,
          totalSites: summary.totalSites,
          successfulSites: summary.successfulSites,
          successRate:
            summary.totalSites > 0
              ? Math.round((summary.successfulSites / summary.totalSites) * 100)
              : 0,
          lastUpdate: summary.generatedAt,
        }

        loadedCategories.push(categoryData)

        // Aggiorna progresso e UI
        loadingProgress.value.current = i + 1
        categoriesData.value = [...loadedCategories].sort((a, b) => a.name.localeCompare(b.name))
      } catch (error) {
        console.warn(`Errore nel caricamento summary per ${categoryName}:`, error)

        // Aggiungi comunque la categoria con dati di fallback
        loadedCategories.push({
          name: categoryName,
          totalSites: 0,
          successfulSites: 0,
          successRate: 0,
          lastUpdate: new Date().toISOString(),
        })

        loadingProgress.value.current = i + 1
        categoriesData.value = [...loadedCategories].sort((a, b) => a.name.localeCompare(b.name))
      }
    }
  } catch (error: any) {
    console.error('Errore nel caricamento dei dati:', error)
    hasError.value = true
    errorMessage.value = error.response?.data?.message || error.message || 'Errore sconosciuto'
  } finally {
    isLoading.value = false
  }
}

onMounted(() => {
  loadAllData()
})
</script>

<style scoped>
.icon--small {
  height: 18px;
  vertical-align: middle;
  margin-right: 5px;
}

.card {
  border: none;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s;
}

.card:hover {
  transform: translateY(-2px);
}

.alert {
  border-radius: 8px;
}

.btn-outline-primary {
  border-color: #1c254f;
  color: #1c254f;
}

.btn-outline-primary:hover {
  background-color: #1c254f;
  border-color: #1c254f;
}

.loading-banner {
  background-color: #f8f9fa;
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid #dee2e6;
}

.loading-spinner {
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #1c254f;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.progress-bar {
  background-color: #1c254f;
}

.success-rate-high {
  color: #28a745;
}

.success-rate-medium {
  color: #ffc107;
}

.success-rate-low {
  color: #dc3545;
}
</style>
